import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, Injector, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { StorageService } from '@td/core';
import Keycloak from 'keycloak-js';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import { ROLE_COURT_ADMIN } from './auth.constants';
import { createDeferred } from './deferred';
import { type UserInfo } from './auth.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly keycloak = inject(Keycloak);
  private readonly storage = inject(StorageService);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly injector = inject(Injector);

  private userInfoPromise?: Promise<void>;
  private readonly ready = createDeferred();

  readonly name = signal('');
  readonly roles = signal<Array<string>>([]);
  readonly isCourtAdmin = computed(() => this.hasRole(ROLE_COURT_ADMIN));

  init(): void {
    effect(
      () => {
        const { type, args } = this.keycloakSignal();

        if (type === KeycloakEventType.Ready) {
          void this.onAuthResolved(Boolean(args));
        } else if (type === KeycloakEventType.AuthSuccess) {
          void this.loadUserInfo();
        } else if (type === KeycloakEventType.AuthLogout) {
          this.reset();
        } else if (type === KeycloakEventType.TokenExpired) {
          void this.keycloak.updateToken(60);
        }
      },
      { injector: this.injector },
    );
  }

  whenReady(): Promise<void> {
    return this.ready.promise;
  }

  async logout(): Promise<void> {
    this.storage.removeItem('isLoginAlertShown');
    await firstValueFrom(this.http.post<void>('api/v1/court/user/logout', null)).catch(() => undefined);
    await this.keycloak.logout();
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  private async onAuthResolved(authenticated: boolean): Promise<void> {
    if (authenticated) {
      await this.loadUserInfo();
    }

    this.ready.resolve();
  }

  private loadUserInfo(): Promise<void> {
    if (!this.userInfoPromise) {
      this.userInfoPromise = this.fetchUserInfo();
    }

    return this.userInfoPromise;
  }

  private async fetchUserInfo(): Promise<void> {
    const userInfo = (await this.keycloak.loadUserInfo()) as UserInfo;
    this.name.set(userInfo.name ?? '');
    this.roles.set(userInfo.resource_access?.['backend-webapp']?.roles ?? []);
  }

  private reset(): void {
    this.userInfoPromise = undefined;
    this.name.set('');
    this.roles.set([]);
  }
}
